<script setup lang="ts">
import api from "@/api";
import {
	TAG_TYPES,
	type TFieldOfStudyView,
	type TFieldOfStudyViewExpandable,
	type TPagination,
	type TTag,
} from "@/assets/js/globalTypes";
import {
	debounce,
	formatPagination,
	getRequestPagination,
	updatePagination,
} from "@/assets/js/helpers";

import {
	openConfirmDeleteModal,
	openCreateFosModal,
	openCreateTagModal,
} from "@/components/modals/modalDefinitions";
import { useToastStore } from "@/store/toast";
import type { MenuItem } from "primevue/menuitem";
import type { PageState } from "primevue/paginator";
definePage({
	name: "admin.system.subjects",
	meta: {
		permissions: "view-system-management",
	},
});

const fieldOfStudy = ref<TFieldOfStudyViewExpandable[]>([]);
const naturesSearch = ref("");
const fieldOfStudySearch = ref("");

const toastStore = useToastStore();

const filteredFos = computed(() => {
	const searchTerm = fieldOfStudySearch.value.toLowerCase().trim();

	if (!searchTerm) {
		return fieldOfStudy.value;
	}

	function filterTree(list) {
		return list
			.map((item) => {
				const copy: TFieldOfStudyViewExpandable = reactive({ ...item });
				if (copy.childs) {
					copy.childs = filterTree(item.childs);
					if (!(item.childs && item.childs.length)) {
						copy.childs = null;
					}
				}
				const match = copy.sfp_name.toLowerCase().includes(searchTerm);
				if (copy.childs && copy.childs.length) {
					copy.expanded = true;
				}
				return match || (copy.childs && copy.childs.length) ? copy : null;
			})
			.filter(Boolean);
	}

	return filterTree(fieldOfStudy.value);
});

const key = ref(0);

const natures = ref<TTag[]>([]);
const naturePagination = ref<TPagination | null>(null);

async function getFieldsOfStudies() {
	try {
		const res = await api.getFieldsOfStudy();

		const addExpand = (el) => {
			el.expanded = el.expanded || false;
			el.edit = el.edit || false;
			if (el.childs) {
				for (const element of el.childs) {
					addExpand(element);
				}
			}
		};
		fieldOfStudy.value = (res.data.data as TFieldOfStudyView[]).map((el) => {
			const copy: TFieldOfStudyViewExpandable = {
				...(el as TFieldOfStudyViewExpandable),
				edit: false,
				expanded: false,
			};
			addExpand(copy);
			return copy;
		});
	} catch (error) {
		console.error(error);
	}
}

async function getNatures(pagination?: PageState) {
	if (pagination) {
		const updatedPagination = formatPagination(pagination);
		updatePagination(naturePagination, updatedPagination);
	}
	try {
		const res = await api.getAllNatures({
			tag_name: naturesSearch.value,
			...getRequestPagination(naturePagination.value),
		});
		natures.value = res.data.data || [];
		naturePagination.value = res.data.meta.pagination;
	} catch (error) {
		console.error(error);
	}
}

const getNaturesDebounced = debounce(getNatures, 500, false);

watch(naturesSearch, getNaturesDebounced);

function openFosModal(event?: {
	fos?: TFieldOfStudyViewExpandable;
	parents?: MenuItem[];
}) {
	const payload = {
		sfpParentId: null,
		breadcrumbs: [],
		onConfirm: getFieldsOfStudies,
	};
	if (event && event.fos) {
		payload.sfpParentId = event.fos.sfp_id;
	}
	if (event && event.parents) {
		payload.breadcrumbs = event.parents;
	}
	openCreateFosModal(payload);
}

onMounted(() => {
	getFieldsOfStudies();
	getNatures();
});

function callOpenCreateTagModal() {
	openCreateTagModal({ tagType: TAG_TYPES.SCHOOL_TYPE, onConfirm: getNatures });
}

function openEditTagModal(tag: TTag) {
	openCreateTagModal({
		tagId: tag.tag_id,
		tagType: TAG_TYPES.SCHOOL_TYPE,
		onConfirm: getNatures,
	});
}

function openConfrimDeleteTagModal(tag: TTag) {
	openConfirmDeleteModal({
		name: tag.tag_name,
		data: tag,
		onConfirm: async () => {
			try {
				const res = await api.deleteTag({
					tag_id: tag.tag_id,
					tag_type: tag.tgt_code,
				});
				toastStore.openToastSuccess(res.data.message);
				getNatures();
			} catch (e) {
				toastStore.openToastError(e.response.data.message);
			}
		},
	});
}
</script>
<template>
	<div id="subjects-page">
		<div class="item">
			<h1>{{ $t("app.school.fieldOfStudy", 2) }}</h1>
			<div class="info-wrap">
				<p-input-text
					id="fos-search-text"
					v-model="fieldOfStudySearch"
					:placeholder="$t('app.base.search')"
				/>
				<p-button @click="openFosModal()">
					<i-ic-outline-add class="large" />

					{{ $t("app.base.add", 2).trim() }}
				</p-button>
			</div>
			<fields-of-study-list
				:key="key"
				:field-of-studies-prop="filteredFos"
				:can-add="true"
				@add-new-child="openFosModal"
				@refresh="getFieldsOfStudies"
			/>
		</div>
		<div class="item">
			<h2>{{ $t("app.tags." + TAG_TYPES.SCHOOL_TYPE.toLowerCase(), 2) }}</h2>
			<div class="info-wrap">
				<p-input-text
					id="natures-search"
					v-model="naturesSearch"
					:placeholder="$t('app.base.search')"
				/>
				<p-button @click="callOpenCreateTagModal">
					<i-ic-outline-add class="large" />

					{{ $t("app.base.add", 2).trim() }}
				</p-button>
			</div>
			<system-tag-list
				:tag-type="TAG_TYPES.SCHOOL_TYPE"
				:tags="natures"
				:pagination="naturePagination"
				@change-page="getNatures"
				@delete-tag="openConfrimDeleteTagModal"
				@edit-tag="openEditTagModal"
			/>
		</div>
	</div>
</template>

<style scoped lang="scss">
#subjects-page {
	display: flex;
	gap: 3rem;
	padding: 1rem;
	.item {
		flex-grow: 1;
		max-width: 30vw;
	}
	.info-wrap {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin: 0.5rem 0;
	}
}
</style>
